import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Loader2, Printer, Download, Mail, X } from 'lucide-react';
import { toast } from 'sonner';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { groupBySupplier } from '../utils/groupBySupplier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORANGE = '#ee7203';

const money = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const unitLabel = (u) => (u === 'M2' ? 'm²' : (u || 'Each').toLowerCase() === 'each' ? 'each' : u);

/** Checkbox that can show the "some selected" (indeterminate) state. */
function TriCheckbox({ checked, indeterminate = false, onChange, testId }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      ref={(el) => { if (el) el.indeterminate = indeterminate; }}
      onChange={onChange}
      className="w-4 h-4 rounded cursor-pointer flex-shrink-0"
      style={{ accentColor: ORANGE }}
      data-testid={testId}
    />
  );
}

/**
 * Print Price List — opened from Customer Management > Pricing > Print.
 * Choose which of the customer's current prices to include (or Include all), check the customer's address, then Print:
 * the server builds the branded PDF (Encon letterhead, customer name and address, one table per supplier group, tables
 * never split across pages, "Encon Birmingham | customer" header + page number on every page after the first) and it opens
 * in a new tab, ready to print.
 *
 * Props: isOpen, onClose, account { id, name }, groups (the active price groups shown in the Pricing panel).
 */
export default function PrintPriceListModal({ isOpen, onClose, account, groups = [] }) {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const [selected, setSelected] = useState(() => new Set());
  const [address, setAddress] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const busy = printing || saving || emailing;

  const allIds = useMemo(() => groups.flatMap((g) => g.prices.map((p) => p.id)), [groups]);

  // Everything is ticked when the window opens ("Include all"); the address is fetched live from the CRM.
  useEffect(() => {
    if (!isOpen) return undefined;
    setSelected(new Set(allIds));
    setAddress('');
    if (!account?.id) return undefined;
    let alive = true;
    setAddressLoading(true);
    api.get(`${API}/zoho/accounts/${account.id}/fresh`)
      .then((r) => { if (alive) setAddress(r.data?.address || ''); })
      .catch(() => { /* the address box stays empty and can be typed in */ })
      .finally(() => { if (alive) setAddressLoading(false); });
    return () => { alive = false; };
  }, [isOpen, account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const buildPayload = () => ({
    account_id: account?.id || null,
    customer_name: account?.name || '',
    customer_address: address,
    price_ids: allIds.filter((id) => selected.has(id)),
  });

  const readErrorDetail = async (e, fallback) => {
    try {
      const text = await e.response.data.text();
      return JSON.parse(text).detail || fallback;
    } catch (_) {
      return e?.response?.data?.detail || fallback;
    }
  };

  const total = allIds.length;
  const count = allIds.filter((id) => selected.has(id)).length;
  const allSelected = total > 0 && count === total;

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleGroup = (group) => {
    const ids = group.prices.map((p) => p.id);
    const every = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => { if (every) next.delete(id); else next.add(id); });
      return next;
    });
  };

  const handlePrint = async () => {
    if (count === 0) return toast.error('Select at least one product');
    // Open the tab straight away (inside the click) so pop-up blockers allow it, then point it at the PDF.
    const win = window.open('', '_blank');
    if (win) {
      try { win.document.write('<p style="font-family:Arial,sans-serif;padding:24px;color:#1f2937">Preparing price list…</p>'); } catch (_) { /* ignore */ }
    }
    setPrinting(true);
    try {
      const res = await api.post(`${API}/customer-pricing/print-pdf`, buildPayload(), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (win) {
        win.location.href = url;
      } else {
        toast.error('Pop-up blocked — please allow pop-ups for this site and press Print again');
        window.URL.revokeObjectURL(url);
        return;
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 120000);
      onClose?.();
    } catch (e) {
      if (win) win.close();
      toast.error(await readErrorDetail(e, 'Failed to create the price list'));
    } finally {
      setPrinting(false);
    }
  };

  // Same PDF as Print, but downloaded straight to the browser's downloads instead of opened in a tab.
  const handleSave = async () => {
    if (count === 0) return toast.error('Select at least one product');
    setSaving(true);
    try {
      const res = await api.post(`${API}/customer-pricing/print-pdf`, buildPayload(), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const safeName = (account?.name || 'Customer').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Customer';
      const link = document.createElement('a');
      link.href = url;
      link.download = `Price_List_${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      toast.success('Price list saved');
      onClose?.();
    } catch (e) {
      toast.error(await readErrorDetail(e, 'Failed to save the price list'));
    } finally {
      setSaving(false);
    }
  };

  // Emails the same PDF to the logged-in user's own inbox (same pattern as "Send to My Email" on a quote).
  const handleEmail = async () => {
    if (count === 0) return toast.error('Select at least one product');
    setEmailing(true);
    try {
      const res = await api.post(`${API}/customer-pricing/email-pdf`, buildPayload());
      toast.success(res.data?.message || 'Price list emailed');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to email the price list');
    } finally {
      setEmailing(false);
    }
  };

  return createPortal(
    <div
      className="fixed left-0 right-0 bottom-0 flex items-center justify-center p-4 bg-black/50"
      style={{ top: modalUiV2 ? activityBar.headerHeight : 0, zIndex: 130 }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
      data-testid="print-price-list-overlay"
    >
      <div
        className="w-full max-w-[720px] max-h-full flex flex-col rounded-3xl bg-white shadow-2xl border-2 border-orange-300 overflow-hidden"
        data-testid="print-price-list-modal"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
            >
              <Printer className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-extrabold text-gray-900 leading-tight">Print Price List</h3>
              <p className="text-[11px] text-gray-500 truncate">{account?.name}</p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-4 flex-shrink-0">
          <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-2">
            Customer address (prints under their name)
            {addressLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
          </Label>
          <textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={3}
            placeholder="Customer address"
            className="mt-1 w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none resize-none"
            data-testid="print-price-list-address"
          />
        </div>

        <div className="px-5 pt-3 pb-1 flex items-center gap-3 flex-shrink-0">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <TriCheckbox checked={allSelected} indeterminate={count > 0 && !allSelected} onChange={toggleAll} testId="print-price-list-include-all" />
            <span className="text-sm font-bold text-gray-900">Include all products</span>
          </label>
          <span className="ml-auto text-xs text-gray-500" data-testid="print-price-list-count">{count} of {total} selected</span>
        </div>

        <div className="px-5 pb-3 flex-1 min-h-0 overflow-y-auto space-y-3" data-testid="print-price-list-groups">
          {groups.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-400">This customer has no prices on file yet.</div>
          ) : groupBySupplier(groups).map((s) => {
            const supplierIds = s.prices.map((p) => p.id);
            const supplierPicked = supplierIds.filter((id) => selected.has(id)).length;
            return (
              <section key={s.key} className="rounded-xl border border-gray-200 overflow-hidden" data-testid={`print-price-list-supplier-${s.testKey}`}>
                <label className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none" style={{ backgroundColor: ORANGE }}>
                  <TriCheckbox checked={supplierPicked === supplierIds.length} indeterminate={supplierPicked > 0 && supplierPicked < supplierIds.length} onChange={() => toggleGroup(s)} testId={`print-price-list-supplier-select-${s.testKey}`} />
                  <span className="text-xs font-bold text-white uppercase tracking-wide">{s.supplier}</span>
                  <span className="text-[11px] text-white/80 ml-auto flex-shrink-0">{supplierPicked}/{supplierIds.length}</span>
                </label>
                {s.groups.map((g) => {
                  const ids = g.prices.map((p) => p.id);
                  const picked = ids.filter((id) => selected.has(id)).length;
                  return (
                    <div key={`${g.supplier}|${g.prefix}`} className="border-t border-orange-100">
                <label className="flex items-center gap-2 px-3 py-1.5 cursor-pointer select-none bg-white border-b border-orange-200">
                  <TriCheckbox checked={picked === ids.length} indeterminate={picked > 0 && picked < ids.length} onChange={() => toggleGroup(g)} testId={`print-price-list-group-${g.prefix}`} />
                  <span className="text-[11px] font-mono font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">{g.prefix}</span>
                  {g.sub_category && <span className="text-[11px] text-gray-600 truncate">— {g.sub_category}</span>}
                  <span className="text-[11px] text-gray-400 ml-auto flex-shrink-0">{picked}/{ids.length}</span>
                </label>
                <div>
                  {g.prices.map((p) => (
                    <label
                      key={p.id}
                      className="grid grid-cols-[20px_100px_minmax(0,1fr)_90px] gap-2 items-center px-3 py-1.5 border-t border-gray-100 cursor-pointer hover:bg-orange-50/50"
                    >
                      <TriCheckbox checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} testId={`print-price-list-item-${p.code}`} />
                      <span className="font-mono font-bold text-xs text-gray-900 truncate">{p.code}</span>
                      <span className="text-xs text-gray-600 truncate" title={p.description}>{p.description || '—'}</span>
                      <span className="text-right text-xs font-bold text-gray-900 tabular-nums">
                        {money(p.unit_price)}<span className="text-[10px] font-normal text-gray-400"> / {unitLabel(p.unit)}</span>
                      </span>
                    </label>
                  ))}
                </div>
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-orange-100 flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-xl h-10 px-4 text-sm" data-testid="print-price-list-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleEmail}
            disabled={busy || count === 0}
            variant="outline"
            className="rounded-xl h-10 px-4 text-sm font-semibold border-2"
            style={{ borderColor: ORANGE, color: ORANGE }}
            data-testid="print-price-list-email-btn"
          >
            {emailing ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Sending…</> : <><Mail className="w-4 h-4 mr-1.5" />Email Me</>}
          </Button>
          <Button
            onClick={handleSave}
            disabled={busy || count === 0}
            variant="outline"
            className="rounded-xl h-10 px-4 text-sm font-semibold border-2"
            style={{ borderColor: ORANGE, color: ORANGE }}
            data-testid="print-price-list-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Saving…</> : <><Download className="w-4 h-4 mr-1.5" />Save</>}
          </Button>
          <Button
            onClick={handlePrint}
            disabled={busy || count === 0}
            className="rounded-xl h-10 px-5 text-sm text-white font-semibold"
            style={{ backgroundColor: ORANGE }}
            data-testid="print-price-list-print-btn"
          >
            {printing ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Creating…</> : <><Printer className="w-4 h-4 mr-1.5" />Print{count > 0 ? ` (${count})` : ''}</>}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
